import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import {
  ChargebeeZatcaInvoiceDTO,
  ChargebeeZatcaInvoicesResponseDTO,
  ChargebeeZatcaReturnInvoicesResponseDTO,
  MappedBillingDocumentWithPaymentReceiptData,
  PaymentStatus,
} from './types';
import {
  InvoiceZatcaStatusConstantOnFE,
  InvoiceZatcaType,
} from '../shared/types';
import { BillingDocumentTypes } from '../accounts/constants';
import { PlanClassification, PlanItemDTO } from '../accounts/types';

const API_URL = `/api/nucleus`;

@Injectable()
export class InvoicesService {
  constructor(private http: HttpClient) {}

  getInvoices(
    page: number,
    limit: number,
    sortAsc: boolean,
    searchParam?: string,
    filters?: Record<string, any>,
    searchField: string = 'chargebeeInvoiceId',
  ): Observable<ChargebeeZatcaInvoicesResponseDTO> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    params = params.set('sortAsc', sortAsc);

    if (searchParam) {
      params = params.set('searchParam', searchParam);
      params = params.set('searchField', searchField);
    }

    if (filters) {
      params = params.set('filters', JSON.stringify(filters));
    }

    return this.http.get<ChargebeeZatcaInvoicesResponseDTO>(
      `${API_URL}/admin/invoices`,
      {
        params,
      },
    );
  }

  /**
   * Check if merchant has non-Rewaapay/Geidea invoices
   * Filters out RP_ and PG_ subscription invoices to only count main subscription invoices
   */
  fetchInvoicesByEmail(email: string): Observable<boolean> {
    if (!email) {
      return of(false);
    }

    return this.getInvoices(1, 100, false, email).pipe(
      map((response) => {
        // Filter out Rewaa Pay (RP_) and Geidea (PG_) subscription invoices
        // Only consider invoices from main subscriptions (rw_*)
        const nonRPPGInvoices = response.data.filter((invoice) => {
          const subscriptionId =
            (invoice as ChargebeeZatcaInvoiceDTO & { subscriptionId?: string })
              .subscriptionId || '';
          return (
            !subscriptionId.startsWith('RP_') &&
            !subscriptionId.startsWith('PG_')
          );
        });
        return nonRPPGInvoices.length > 0;
      }),
      catchError(() => of(false)),
    );
  }

  getInvoiceById(
    invoiceId: string,
  ): Observable<MappedBillingDocumentWithPaymentReceiptData> {
    return this.http.get<MappedBillingDocumentWithPaymentReceiptData>(
      `${API_URL}/admin/invoice/${invoiceId}`,
    );
  }

  getCreditNotes(
    page: number,
    limit: number,
    sortAsc: boolean,
    searchParam?: string,
    filters?: Record<string, any>,
    searchField: string = 'chargebeeInvoiceId',
  ): Observable<ChargebeeZatcaReturnInvoicesResponseDTO> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    params = params.set('sortAsc', sortAsc);

    if (searchParam) {
      params = params.set('searchParam', searchParam);
      params = params.set('searchField', searchField);
    }

    if (filters) {
      params = params.set('filters', JSON.stringify(filters));
    }

    return this.http.get<ChargebeeZatcaReturnInvoicesResponseDTO>(
      `${API_URL}/admin/return-invoice`,
      {
        params,
      },
    );
  }

  getCreditNoteById(
    creditNoteId: string,
  ): Observable<MappedBillingDocumentWithPaymentReceiptData> {
    return this.http.get<MappedBillingDocumentWithPaymentReceiptData>(
      `${API_URL}/admin/credit-note/${creditNoteId}`,
    );
  }

  getCreditNoteLabel(zatcaStatus: string, invoiceType: string): string {
    return zatcaStatus === InvoiceZatcaStatusConstantOnFE.Rejected &&
      invoiceType === InvoiceZatcaType.B2B
      ? 'Issue CB Credit Note'
      : 'Issue Credit Note';
  }

  canResubmitInvoiceToZatca(
    invoiceType: string,
    zatcaStatus: string,
    isPreZatca: boolean,
    invoiceAndCreditNotesListingFF: boolean,
  ): boolean {
    if (!invoiceAndCreditNotesListingFF || isPreZatca) return false;

    if (invoiceType === InvoiceZatcaType.B2B)
      return (
        zatcaStatus !== InvoiceZatcaStatusConstantOnFE.Accepted &&
        zatcaStatus !== InvoiceZatcaStatusConstantOnFE.AcceptedWithWarnings
      );

    if (invoiceType === InvoiceZatcaType.B2C)
      return (
        zatcaStatus === InvoiceZatcaStatusConstantOnFE.Failed ||
        zatcaStatus === InvoiceZatcaStatusConstantOnFE.NotSubmitted ||
        zatcaStatus === InvoiceZatcaStatusConstantOnFE.WaitingOnStatus
      );

    return false;
  }

  canResubmitCreditNoteToZatca(
    zatcaStatus: string,
    isPreZatca: boolean,
    referenceInvoiceZatcaStatus: string,
    invoiceAndCreditNotesListingFF: boolean,
  ): boolean {
    if (!invoiceAndCreditNotesListingFF || isPreZatca) return false;

    if (
      referenceInvoiceZatcaStatus !== InvoiceZatcaStatusConstantOnFE.Accepted &&
      referenceInvoiceZatcaStatus !==
        InvoiceZatcaStatusConstantOnFE.AcceptedWithWarnings
    )
      return false;

    return (
      zatcaStatus !== InvoiceZatcaStatusConstantOnFE.Accepted &&
      zatcaStatus !== InvoiceZatcaStatusConstantOnFE.AcceptedWithWarnings &&
      zatcaStatus !== InvoiceZatcaStatusConstantOnFE.Pending
    );
  }

  canIssueCreditNote(
    type: string,
    status: string,
    zatcaStatus: string,
    manualCreditNoteFeatureFlag: boolean,
  ): boolean {
    if (!manualCreditNoteFeatureFlag) return false;
    return (
      type === BillingDocumentTypes.Invoice &&
      status !== 'voided' &&
      status !== PaymentStatus.NotPaid &&
      (zatcaStatus === InvoiceZatcaStatusConstantOnFE.Accepted ||
        zatcaStatus === InvoiceZatcaStatusConstantOnFE.AcceptedWithWarnings ||
        zatcaStatus === InvoiceZatcaStatusConstantOnFE.Rejected)
    );
  }

  getPlansByClassification(
    classifications: PlanClassification[],
  ): Observable<PlanItemDTO[]> {
    const values = Array.isArray(classifications)
      ? classifications
      : [classifications];

    const params = values.reduce(
      (acc, value) => acc.append('classifications', value),
      new HttpParams(),
    );

    return this.http.get<PlanItemDTO[]>(
      `${API_URL}/admin/plans-by-classification`,
      { params },
    );
  }

  // deploy
}
